import type { CsvColumnDef, BulkUploadRowError } from '../types';

interface CsvPreviewTableProps {
  columns: CsvColumnDef[];
  data: Record<string, string>[];
  errors: BulkUploadRowError[];
  maxRows?: number;
}

export function CsvPreviewTable({ columns, data, errors, maxRows = 50 }: CsvPreviewTableProps) {
  const errorMap = new Map<string, string>();
  errors.forEach(e => {
    const key = `${e.row}-${e.field || ''}`;
    errorMap.set(key, e.message);
  });
  const errorRows = new Set(errors.map(e => e.row));

  const displayData = data.slice(0, maxRows);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="overflow-x-auto max-h-80">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Row
              </th>
              {columns.map(col => (
                <th
                  key={col.key}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  {col.label}
                  {col.required && <span className="text-red-500 ml-0.5">*</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayData.map((row, idx) => {
              const rowNum = idx + 2;
              const hasError = errorRows.has(rowNum);
              return (
                <tr
                  key={idx}
                  className={hasError ? 'bg-red-50 dark:bg-red-900/20' : 'bg-white dark:bg-gray-800'}
                >
                  <td className="px-3 py-1.5 text-xs text-gray-400 whitespace-nowrap">
                    {rowNum}
                  </td>
                  {columns.map(col => {
                    const cellError = errorMap.get(`${rowNum}-${col.key}`);
                    return (
                      <td
                        key={col.key}
                        className={`px-3 py-1.5 text-xs whitespace-nowrap ${
                          cellError
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                        title={cellError || undefined}
                      >
                        {row[col.key] || <span className="text-gray-300">-</span>}
                        {cellError && (
                          <span className="ml-1 text-red-500" title={cellError}>!</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.length > maxRows && (
        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 text-xs text-gray-500">
          Showing {maxRows} of {data.length} rows
        </div>
      )}
    </div>
  );
}



